// Shared helpers for parsing DetectionZoneTripwirePoints across all of its
// historical storage shapes. The column has evolved with the tripwire-group
// feature, and a frontend that hasn't been re-deployed since a migration can
// still see an older shape — so every consumer should go through these helpers
// instead of inlining its own parser.
//
//   Current (tw03):     [{"id":"tw_…","points":[[x,y],[x,y]]}, …]
//   Intermediate (tw02): [[[x,y],[x,y]], …]
//   Legacy (pre-tw02):  [[x,y],[x,y]]                  (one tripwire, flat)
//
// All helpers return the canonical current shape. An id is synthesized for
// any entry that doesn't have one — harmless after the tw03 migration, and
// keeps consumers that need an id (for direction lookups) free of branching.

const _isNumber = (v) => typeof v === 'number' && !Number.isNaN(v)
const _isPoint = (p) => Array.isArray(p) && p.length === 2 && _isNumber(p[0]) && _isNumber(p[1])
const _isTripwire = (tw) => Array.isArray(tw) && tw.length === 2 && _isPoint(tw[0]) && _isPoint(tw[1])
const _isTripwireObject = (o) =>
    o && typeof o === 'object' &&
    typeof o.id === 'string' && o.id.length > 0 &&
    _isTripwire(o.points)

const _genId = () => `tw_${Math.random().toString(16).slice(2, 10).padStart(8, '0')}`

const _safeJsonParse = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value !== 'string') return value
    try { return JSON.parse(value) } catch { return null }
}

/**
 * Parse any storage-shape representation of DetectionZoneTripwirePoints into
 * the canonical [{id, points}, ...] list. Accepts a JSON string, an already-
 * parsed array, or null/undefined. Returns [] when nothing valid is present.
 */
export function parseStoredTripwires(value) {
    const data = _safeJsonParse(value)
    if (!Array.isArray(data) || data.length === 0) return []
    // Legacy single-tripwire: a flat [[x,y],[x,y]].
    if (_isTripwire(data)) return [{ id: _genId(), points: data }]
    const out = []
    for (const item of data) {
        if (_isTripwireObject(item)) {
            out.push({ id: item.id, points: item.points })
        } else if (_isTripwire(item)) {
            out.push({ id: _genId(), points: item })
        }
    }
    return out
}

/**
 * Return just the geometry — list of [[x,y],[x,y]] entries — when the caller
 * doesn't care about ids. Useful for rendering / counting where the id has
 * no role.
 */
export function tripwirePointsList(value) {
    return parseStoredTripwires(value).map(tw => tw.points)
}

/**
 * True when the input represents at least one valid tripwire (after parsing
 * and normalizing). Replacement for ad-hoc `Array.isArray(pts) && pts.length === 2`
 * checks that no longer work after tw02 (multi) and tw03 (objects).
 */
export function hasAnyTripwire(value) {
    return parseStoredTripwires(value).length > 0
}
